import IconButton from 'components/atoms/button/IconButton'
import Form from 'components/atoms/form/Form'
import Icon from 'components/atoms/icon/Icon'
import Input from 'components/atoms/input/Input'
import Label from 'components/atoms/input/input-label/InputLabel'
import Comments from 'components/molecules/comments/Comments'
import PrimaryDatePicker from 'components/molecules/datepicker/PrimaryDatePicker'
import { customStyles } from 'lib/helpers/configs/customStyles'
import { IconType } from 'lib/helpers/constants/iconTypes'
import withModal from 'lib/helpers/hoc/withModal'
import { useCreateAppointmentTask } from 'lib/hooks/mutations/Appointments/useCreateAppointmentTask'
import { useCreateFollowUp } from 'lib/hooks/mutations/Leads/useCreateFollowUp'
import { useStoreOwnPending } from 'lib/hooks/mutations/Pendings/useStoreOwnPending'
import React, { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'


interface FollowUpTaskProps {
    closeModal: () => void
    id?: string
    isLead?: boolean
    isOwn?: boolean
}

const FollowUpTask: FC<FollowUpTaskProps> = ({ closeModal, id, isLead = false, isOwn = false }) => {

    const { t } = useTranslation()

    const {
        handleSubmit,
        register,
        formState: { errors },
        control,
        reset,
    } = useForm({
        defaultValues: {
            pending_type: "Other Task",
            other_category: "",
            pending_name: "",
            deadline: null,
        }
    })

    const [comment, setComment] = useState<string>("");
    const [imagePreview, setImagePreview] = useState<any>();
    const [imageBlob, setImageBlob] = useState<Blob | null>(null);

    const handleReset = () => {
        setComment("");
        setImagePreview(null);
        setImageBlob(null);
        reset()
    }


    const { mutateAsync, isLoading } = useCreateAppointmentTask()

    const { mutateAsync: leadFollowUp, isLoading: leadLoading } = useCreateFollowUp()

    const { mutateAsync: ownPending, isLoading: isLoadingOwn } = useStoreOwnPending()

    const onSubmit = (formData: Record<string, any>): void => {

        const dataWithId = {
            ...formData,
            ...(isLead ? { lead_id: id } : { appointment_id: id }),
            description: comment,
        };

        if (imageBlob) {
            const reader = new FileReader();
            reader.readAsDataURL(imageBlob);
            reader.onload = () => {
                const dataWithImage = { ...dataWithId, image: reader.result as string };

                if (isOwn) {
                    ownPending(dataWithImage)
                        .then(() => {
                            closeModal()
                        })
                    return
                }

                if (isLead) {
                    leadFollowUp(dataWithImage)
                        .then(() => {
                            closeModal()
                        })
                } else {
                    mutateAsync(dataWithImage)
                        .then(() => {
                            closeModal()
                        })
                }

            };

        } else {

            if (isOwn) {
                ownPending(dataWithId)
                    .then(() => {
                        closeModal()
                    })
                return
            }

            if (isLead) {
                leadFollowUp(dataWithId)
                    .then(() => {
                        closeModal()
                    })
            } else {
                mutateAsync(dataWithId)
                    .then(() => {
                        closeModal()
                    })
            }
        }
    }

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <div className='bg-themeOpacity2 px-4 py-5 rounded-[20px] mb-10 flex flex-col gap-5'>
                {!isOwn && (
                <div className="relative">
                    <Label text={t("Issue Type")} />
                    <Controller
                        name="pending_type"
                        control={control}
                        rules={{ required: `${t("This field is required")}` }}
                        render={({ field }) => (
                            <CreatableSelect<any, false>
                                {...field}
                                placeholder={t("Choose issue type")}
                                options={[
                                    {
                                        value: "Other Task",
                                        label: `${t("Other Task")}`,
                                    },
                                ]}
                                isDisabled={true}
                                value={
                                    field.value
                                        ? { value: field.value, label: t(field.value) }
                                        : null
                                }
                                className="cursor-pointer font-inter-regular react-select-container"
                                classNamePrefix="react-select"
                                onChange={(newValue: any, actionMeta: any) => {
                                    if (
                                        actionMeta.action === "select-option" &&
                                        newValue?.value
                                    ) {
                                        field.onChange(newValue.value);
                                    }
                                }}
                                styles={customStyles}
                                isValidNewOption={() => false}
                                menuPosition={"fixed"}
                                menuPortalTarget={document.body}
                            />

                        )}
                    />
                    {errors.pending_type?.message && (
                        <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                            {errors.pending_type?.message as string}
                        </div>
                    )}
                </div>
                )}
                <Input
                    label={t("Title")}
                    placeholder={t("Title")}
                    {...register("pending_name", { required: `${t("This field is required")}` })}
                    error={errors?.pending_name}
                />

                <Controller
                    name="deadline"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <PrimaryDatePicker
                            label={t('Deadline')}
                            startDate={value}
                            setStartDate={(date: string) => onChange(date)}
                            className="md:text-[14px]  !p-[8px]"
                            wrapperClassName="!p-[8px] !w-[30vh]"
                        />
                    )}
                />
                <div>
                    <Label text={t("Description")} />
                    <Comments
                        handleSend={() => { }}
                        data={[]}
                        setImageBlob={setImageBlob}
                        comment={comment}
                        setComment={setComment}
                        imagePreview={imagePreview}
                        setImagePreview={setImagePreview}
                        pathForImg={'appointment/get-notes-img/'}
                        showSendButton={false}
                        imageInputId="task-image-upload"
                    />
                </div>
            </div>
            <div className="flex gap-4 justify-end">
                <IconButton type='button' className='sm:min-w-[120px]' onClick={handleReset} secondary disabled={isLoading || leadLoading || isLoadingOwn}>
                    {t("Reset")}
                </IconButton>
                <IconButton icon={<Icon iconType={IconType.PLUS_ICON} />} className='sm:min-w-[150px]' disabled={comment.length === 0 || isLoading || leadLoading || isLoadingOwn} >
                    {t("Create")}
                </IconButton>
            </div>
        </Form>
    )
}

export default withModal(FollowUpTask)